import { useState } from "react";
import React from "react";
import { createPortal } from "react-dom";
import { Building2, AlertTriangle, Clock, CalendarClock, TrendingUp, PackageX, Activity, X, Search, ChevronRight, ArrowLeft, Download, Mail, Copy, Check, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getBranchEntries, getMetricValue, METRIC, type DashboardPharmacy } from "@/pages/MultiBranchDashboard/dashboardMetrics";
import { getExpiringItems } from "@/dispatchers/titanTabDispatchers";
import TableRowsSkeleton from "./TableRowsSkeleton";

const BRANCH_COLORS = ["#7c3aed", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];

type ItemCategory = "Normal" | "CD" | "ERD" | "CD-ERD";

type ItemStage = "Received" | "Labelled" | "Dispensed" | "Checked" | "Awaiting Collection" | "Owing";

interface ExpiringItem {
  id: string;
  drug: string;
  pack: string;
  patient: string;
  rxDate: string;
  expiryDate: string;
  daysLeft: number;
  dtPrice: number;
  nhsReimbursement: number;
  costPrice: number;
  stage: ItemStage;
  epsBarcode: string;
  prescriber: string;
  treatmentType: "Acute" | "Repeat";
  branch: string;
  quantity: string;
  category: ItemCategory;
  issueNumber?: number;
  totalIssues?: number;
}

const patientInitials = (name: string) =>
  name.split(" ").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + ".").join("");

const csvCell = (value: string) => '"' + value.split('"').join('""') + '"';

const dispensingWindowDays = (item: ExpiringItem): number => {
  const isFirstIssue = !item.issueNumber || item.issueNumber === 1;
  switch (item.category) {
    case "CD":      return 28;
    case "CD-ERD":  return isFirstIssue ? 28  : 365;
    case "ERD":     return isFirstIssue ? 183 : 365;
    case "Normal":
    default:        return 183;
  }
};

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  "Normal":  "Normal",
  "CD":      "Controlled Drug",
  "ERD":     "ERD",
  "CD-ERD":  "CD ERD",
};

const CATEGORY_COLORS: Record<ItemCategory, { bg: string; text: string; bgLight: string; textLight: string }> = {
  "Normal":  { bg: "bg-sky-500/15",     text: "text-sky-400",     bgLight: "bg-sky-100",     textLight: "text-sky-700" },
  "CD":      { bg: "bg-rose-500/15",    text: "text-rose-400",    bgLight: "bg-rose-100",    textLight: "text-rose-700" },
  "ERD":     { bg: "bg-emerald-500/15", text: "text-emerald-400", bgLight: "bg-emerald-100", textLight: "text-emerald-700" },
  "CD-ERD":  { bg: "bg-fuchsia-500/15", text: "text-fuchsia-400", bgLight: "bg-fuchsia-100", textLight: "text-fuchsia-700" },
};

const STAGE_COLORS: Record<string, { bg: string; text: string; bgLight: string; textLight: string }> = {
  Received:              { bg: "bg-slate-500/15",  text: "text-slate-400",  bgLight: "bg-slate-100",  textLight: "text-slate-700" },
  Labelled:              { bg: "bg-blue-500/15",   text: "text-blue-400",   bgLight: "bg-blue-100",   textLight: "text-blue-700" },
  Dispensed:             { bg: "bg-indigo-500/15", text: "text-indigo-400", bgLight: "bg-indigo-100", textLight: "text-indigo-700" },
  Checked:               { bg: "bg-violet-500/15", text: "text-violet-400", bgLight: "bg-violet-100", textLight: "text-violet-700" },
  "Awaiting Collection": { bg: "bg-amber-500/15",  text: "text-amber-400",  bgLight: "bg-amber-100",  textLight: "text-amber-700" },
  Owing:                 { bg: "bg-red-500/15",    text: "text-red-400",    bgLight: "bg-red-100",    textLight: "text-red-700" },
};

const CATEGORY_VALUES: ItemCategory[] = ["Normal", "CD", "ERD", "CD-ERD"];
const STAGE_VALUES: ItemStage[] = ["Received", "Labelled", "Dispensed", "Checked", "Awaiting Collection", "Owing"];

const normaliseCategory = (raw: any): ItemCategory => {
  const value = String(raw ?? "").trim().toLowerCase().replace(/[\s_]+/g, "-");
  return CATEGORY_VALUES.find(c => c.toLowerCase() === value) ?? "Normal";
};

const normaliseStage = (raw: any): ItemStage => {
  const value = String(raw ?? "").trim().toLowerCase();
  return STAGE_VALUES.find(s => s.toLowerCase() === value) ?? "Received";
};

const toNumber = (raw: any): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const isRealDate = (d: any) => d && !String(d).startsWith("0001");

const fmtShortDate = (d: any) =>
  isRealDate(d) ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";

const fmtIsoDate = (d: any) => (isRealDate(d) ? String(d).slice(0, 10) : "");

// get-expiring-items returns one row per prescription item, already carrying the
// expiry window and the pricing the table renders.
const mapApiExpiringItem = (r: any, branch: string): ExpiringItem => ({
  id: r?.prescriptionItemId ?? "",
  drug: r?.drug || "—",
  pack: r?.pack || "—",
  patient: r?.patientName || "—",
  rxDate: fmtIsoDate(r?.rxDate),
  expiryDate: fmtIsoDate(r?.expiryDate),
  daysLeft: toNumber(r?.daysLeft),
  dtPrice: toNumber(r?.dtPrice),
  nhsReimbursement: toNumber(r?.nhsReimbursement),
  costPrice: toNumber(r?.costPrice),
  stage: normaliseStage(r?.stage),
  epsBarcode: r?.epsBarcode || r?.barcode || "—",
  prescriber: r?.prescriber || "—",
  treatmentType: String(r?.treatmentType ?? "").toLowerCase().includes("repeat") ? "Repeat" : "Acute",
  branch,
  quantity: r?.quantity != null ? String(r.quantity) : "—",
  category: normaliseCategory(r?.category),
});

export default function ExpiringItemsTab({ isDarkMode, selectedBranches, onBranchChange }: { isDarkMode: boolean; selectedBranches: string[]; onBranchChange: (id: string) => void }) {
  const { titanTabState } = useTitanApp() as any;
  // Branch list and the estate overview numbers come from the shared
  // get-dashboard-stats payload, exactly as the claims overview does.
  const dashboardStats: DashboardPharmacy[] = Array.isArray(titanTabState?.dashboardStats) ? titanTabState.dashboardStats : [];
  const pharmacies = getBranchEntries(dashboardStats).map((p, i) => ({
    id: p.pharmacyId,
    name: p.name,
    color: BRANCH_COLORS[i % BRANCH_COLORS.length],
    metrics: p.metrics,
  }));

  const [showOverview, setShowOverview] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "expiring" | "expired">("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | ItemCategory>("all");
  const [urgencyFilter, setUrgencyFilter] = useState<"all" | "critical" | "soon" | "upcoming">("all");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedBarcode, setCopiedBarcode] = useState<string | null>(null);
  const [activeBranch, setActiveBranch] = useState(selectedBranches[0] ?? "");
  const [allItems, setAllItems] = useState<ExpiringItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const copyBarcode = (id: string, barcode: string) => {
    navigator.clipboard.writeText(barcode).then(() => {
      setCopiedBarcode(id);
      setTimeout(() => setCopiedBarcode((prev) => (prev === id ? null : prev)), 1500);
    });
  };

  React.useEffect(() => {
    if (!selectedBranches.includes(activeBranch)) {
      setActiveBranch(selectedBranches[0] ?? "");
    }
  }, [selectedBranches, activeBranch]);

  React.useEffect(() => {
    if (pharmacies.length > 0 && !pharmacies.some(p => p.id === activeBranch)) {
      setActiveBranch(pharmacies[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacies.length]);

  React.useEffect(() => {
    setSelectedIds(new Set());
    setSearchTerm("");
    setStatusFilter("all");
    setCategoryFilter("all");
    setUrgencyFilter("all");
    setStageFilter("all");
  }, [activeBranch]);

  // Items are only fetched once a branch is opened; the overview runs off metrics.
  React.useEffect(() => {
    if (showOverview || !activeBranch) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    getExpiringItems(activeBranch).then((rows: any) => {
      if (cancelled) return;
      if (rows === null) {
        setAllItems([]);
        setLoadError("The expiring items list couldn't be loaded. Please try again.");
      } else {
        setAllItems(rows.map((r: any) => mapApiExpiringItem(r, activeBranch)));
      }
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeBranch, showOverview]);

  const activeBranchData = pharmacies.find((b) => b.id === activeBranch);

  const isExpired = (i: ExpiringItem) => i.daysLeft < 0;

  const filteredItems = allItems.filter((item) => {
    if (statusFilter === "expired" && !isExpired(item)) return false;
    if (statusFilter === "expiring" && isExpired(item)) return false;
    if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
    if (urgencyFilter === "critical" && (isExpired(item) || item.daysLeft > 2)) return false;
    if (urgencyFilter === "soon" && (item.daysLeft < 3 || item.daysLeft > 7)) return false;
    if (urgencyFilter === "upcoming" && item.daysLeft <= 7) return false;
    if (stageFilter !== "all" && item.stage !== stageFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return item.drug.toLowerCase().includes(s) || item.patient.toLowerCase().includes(s) || item.epsBarcode.toLowerCase().includes(s);
    }
    return true;
  });

  const expiredCount  = allItems.filter(isExpired).length;
  const expiringCount = allItems.filter((i) => !isExpired(i)).length;
  const criticalCount = allItems.filter((i) => !isExpired(i) && i.daysLeft <= 2).length;
  const soonCount = allItems.filter((i) => i.daysLeft >= 3 && i.daysLeft <= 7).length;
  const upcomingCount = allItems.filter((i) => i.daysLeft > 7).length;

  const categoryCounts: Record<ItemCategory, number> = {
    "Normal":  allItems.filter((i) => i.category === "Normal").length,
    "CD":      allItems.filter((i) => i.category === "CD").length,
    "ERD":     allItems.filter((i) => i.category === "ERD").length,
    "CD-ERD":  allItems.filter((i) => i.category === "CD-ERD").length,
  };

  const totalDtValue = allItems.reduce((sum, i) => sum + i.dtPrice, 0);
  const totalNhsValue = allItems.reduce((sum, i) => sum + i.nhsReimbursement, 0);
  const totalCostValue = allItems.reduce((sum, i) => sum + i.costPrice, 0);
  const totalMargin = totalNhsValue - totalCostValue;
  const atRiskValue = allItems.filter((i) => !isExpired(i) && i.daysLeft <= 2).reduce((sum, i) => sum + i.dtPrice, 0);
  const expiredValue = allItems.filter(isExpired).reduce((sum, i) => sum + i.dtPrice, 0);
  const awaitingCollection = allItems.filter((i) => i.stage === "Awaiting Collection").length;
  const notYetDispensed = allItems.filter((i) => i.stage === "Received" || i.stage === "Labelled").length;

  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleAll = () => {
    if (selectedIds.size === filteredItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredItems.map((i) => i.id)));
  };

  const getDaysLeftColor = (days: number) => {
    if (days < 0)  return isDarkMode ? "text-red-300 bg-red-500/25"  : "text-red-800 bg-red-200";
    if (days === 0) return isDarkMode ? "text-red-400 bg-red-500/15" : "text-red-700 bg-red-100";
    if (days <= 2) return isDarkMode ? "text-orange-400 bg-orange-500/15" : "text-orange-700 bg-orange-100";
    if (days <= 7) return isDarkMode ? "text-amber-400 bg-amber-500/15" : "text-amber-700 bg-amber-100";
    return isDarkMode ? "text-green-400 bg-green-500/15" : "text-green-700 bg-green-100";
  };

  const getDaysLabel = (days: number) => {
    if (days < 0)  return `EXPIRED ${Math.abs(days)}d`;
    if (days === 0) return "TODAY";
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  // Shared by CSV export and email: no NHS reimbursement, patient reduced to initials.
  const exportHeaders = ["Days Left", "Status", "Type", "Drug", "Pack", "Quantity", "Patient", "Prescriber", "Stage", "EPS Barcode", "Rx Date", "Expiry Date"];

  const buildExportRows = (items: ExpiringItem[]) =>
    [...items]
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .map((i) => [
        String(i.daysLeft),
        isExpired(i) ? "Expired" : "Expiring",
        CATEGORY_LABELS[i.category],
        i.drug,
        i.pack,
        i.quantity,
        patientInitials(i.patient),
        i.prescriber,
        i.stage,
        i.epsBarcode,
        i.rxDate,
        i.expiryDate,
      ]);

  const exportCsv = (items: ExpiringItem[]) => {
    const csv = [exportHeaders, ...buildExportRows(items)].map((row) => row.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expiring-items-${activeBranchData?.name ?? activeBranch}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const emailData = (items: ExpiringItem[]) => {
    const rows = buildExportRows(items);
    const subject = `Expiring & Expired Items — ${activeBranchData?.name ?? activeBranch}`;
    const body = [
      `${rows.length} item(s) · ${items.filter(isExpired).length} expired`,
      "",
      "Days left | Drug | Patient | Stage | EPS barcode | Expiry",
      ...rows.map((r) => [r[0], r[3], r[6], r[8], r[9], r[11]].join(" | ")),
    ].join("\n");
    const link = document.createElement("a");
    link.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    link.click();
  };

  const selectedItems = filteredItems.filter((i) => selectedIds.has(i.id));

  if (showOverview) {
    const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
    const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
    // Overview columns map onto the Expiring Items block of the dashboard metrics.
    const overviewRows = pharmacies.map(b => {
      const expired = getMetricValue(b as any, METRIC.EXPIRED_ITEMS, NaN);
      const expiring = getMetricValue(b as any, METRIC.EXPIRING_ITEMS, NaN);
      const critical = getMetricValue(b as any, METRIC.CRITICAL_EXPIRING, NaN);
      const actionNeeded = getMetricValue(b as any, METRIC.EXPIRING_ACTION_NEEDED, NaN);
      const atRisk = getMetricValue(b as any, METRIC.AT_RISK_VALUE_EXPIRING, NaN);
      const total = (Number.isFinite(expired) ? expired : 0) + (Number.isFinite(expiring) ? expiring : 0);
      const hasData = Number.isFinite(expired) || Number.isFinite(expiring);
      return { branch: b, expired, expiring, critical, actionNeeded, atRisk, total, hasData };
    });
    const estateExpired = overviewRows.reduce((s, r) => s + (Number.isFinite(r.expired) ? r.expired : 0), 0);
    const estateExpiring = overviewRows.reduce((s, r) => s + (Number.isFinite(r.expiring) ? r.expiring : 0), 0);
    return (
      <div>
        <div className="mb-6">
          <h2 className={`text-lg font-semibold ${textPrimary}`}>Expiring Items — All Branches</h2>
          <p className={`text-sm mt-0.5 ${textSecondary}`}>{estateExpiring} expiring · {estateExpired} expired across estate</p>
        </div>
        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={isDarkMode ? "bg-gray-800/60" : "bg-gray-50"}>
                {["Branch", "Total", "Expired", "Expiring", "Critical (≤2d)", "Action Needed", "At-Risk Value", ""].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-100"}`}>
              {overviewRows.map(({ branch: b, expired, expiring, critical, actionNeeded, atRisk, total, hasData }) => (
                <tr
                  key={b.id}
                  onClick={() => { setActiveBranch(b.id); onBranchChange(b.id); setShowOverview(false); }}
                  className={`cursor-pointer transition-colors ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                >
                  <td className={`px-4 py-3.5 font-medium ${textPrimary}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />
                      {b.name}
                    </div>
                  </td>
                  <td className={`px-4 py-3.5 ${hasData ? textPrimary : textSecondary}`}>{hasData ? total : "—"}</td>
                  <td className={`px-4 py-3.5 font-semibold ${expired > 0 ? "text-red-500" : textSecondary}`}>{expired > 0 ? expired : "—"}</td>
                  <td className={`px-4 py-3.5 ${expiring > 0 ? (isDarkMode ? "text-amber-400" : "text-amber-600") : textSecondary}`}>{expiring > 0 ? expiring : "—"}</td>
                  <td className={`px-4 py-3.5 font-semibold ${critical > 0 ? "text-red-500" : textSecondary}`}>{critical > 0 ? critical : "—"}</td>
                  <td className={`px-4 py-3.5 font-semibold ${actionNeeded > 0 ? (isDarkMode ? "text-amber-400" : "text-amber-600") : textSecondary}`}>{actionNeeded > 0 ? actionNeeded : "—"}</td>
                  <td className={`px-4 py-3.5 font-mono ${atRisk > 0 ? "text-red-500 font-semibold" : textSecondary}`}>{atRisk > 0 ? `£${atRisk.toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3.5 text-right">
                    <ChevronRight className={`w-4 h-4 inline-block ${isDarkMode ? "text-gray-600" : "text-gray-400"}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setShowOverview(true)} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg cursor-pointer transition-colors ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <ArrowLeft className="w-3.5 h-3.5" /> All Branches
          </button>
        </div>
        <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Expiring &amp; Expired Items</h2>
        <p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {expiringCount} expiring · {expiredCount} expired prescription items for {activeBranchData?.name ?? activeBranch}
        </p>
        <div className="flex items-center gap-2 mt-2.5">
          <Building2 className={`w-4 h-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
          <select
            value={activeBranch}
            onChange={(e) => { setActiveBranch(e.target.value); onBranchChange(e.target.value); }}
            className={`text-sm font-medium rounded-lg border px-3 py-1.5 cursor-pointer outline-none transition-colors ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-600" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}
          >
            {pharmacies.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {isLoading && <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />}
        </div>
        {loadError && (
          <p className={`text-xs mt-2 ${isDarkMode ? "text-red-400" : "text-red-600"}`}>{loadError}</p>
        )}
      </div>

      {/* Headline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <button
          onClick={() => setStatusFilter(statusFilter === "expired" ? "all" : "expired")}
          className={`text-left rounded-xl p-4 border transition-colors ${
            statusFilter === "expired"
              ? isDarkMode ? "bg-red-500/20 border-red-500/40" : "bg-red-100 border-red-300"
              : isDarkMode ? "bg-red-500/10 border-red-500/20 hover:bg-red-500/15" : "bg-red-50 border-red-200 hover:bg-red-100"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <PackageX className={`w-4 h-4 ${isDarkMode ? "text-red-400" : "text-red-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-red-400" : "text-red-600"}`}>Expired</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{expiredCount}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>£{expiredValue.toFixed(2)} loss</p>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === "expiring" ? "all" : "expiring")}
          className={`text-left rounded-xl p-4 border transition-colors ${
            statusFilter === "expiring"
              ? isDarkMode ? "bg-amber-500/20 border-amber-500/40" : "bg-amber-100 border-amber-300"
              : isDarkMode ? "bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/15" : "bg-amber-50 border-amber-200 hover:bg-amber-100"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className={`w-4 h-4 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>Expiring</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>{expiringCount}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{criticalCount} critical · {soonCount} soon · {upcomingCount} upcoming</p>
        </button>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={`w-4 h-4 ${isDarkMode ? "text-red-400" : "text-red-500"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>At-Risk Value</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-red-300" : "text-red-600"}`}>£{atRiskValue.toFixed(2)}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Items expiring ≤2 days</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className={`w-4 h-4 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Total DT Value</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>£{totalDtValue.toFixed(2)}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Margin: £{totalMargin.toFixed(2)}</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity className={`w-4 h-4 ${isDarkMode ? "text-violet-400" : "text-violet-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Action Needed</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{awaitingCollection + notYetDispensed}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{awaitingCollection} awaiting · {notYetDispensed} not dispensed</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className={`w-4 h-4 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>By Type</span>
          </div>
          <p className={`text-xs leading-5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
            <span className="font-semibold">{categoryCounts.Normal}</span> Normal · <span className="font-semibold">{categoryCounts.CD}</span> CD<br/>
            <span className="font-semibold">{categoryCounts.ERD}</span> ERD · <span className="font-semibold">{categoryCounts["CD-ERD"]}</span> CD ERD
          </p>
        </div>
      </div>

      {/* Category Sub-division */}
      <div className={`rounded-xl p-3 border ${isDarkMode ? "bg-gray-800/40 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold uppercase tracking-wide mr-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Category</span>
          {(["all", "Normal", "CD", "ERD", "CD-ERD"] as const).map((c) => {
            const active = categoryFilter === c;
            const count = c === "all" ? allItems.length : categoryCounts[c];
            const labels: Record<string, string> = { all: "All", Normal: "Normal (6 mo)", CD: "Controlled Drug (28 d)", ERD: "ERD", "CD-ERD": "CD ERD" };
            return (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-700/60 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                }`}
              >
                {labels[c]} <span className={active ? "opacity-80" : "opacity-60"}>{count}</span>
              </button>
            );
          })}
          <div className="flex-1" />
          <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
            ERD: 1st issue dispensed within 6 mo (28 d if CD) · remaining issues within 12 mo
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          {(["all", "expiring", "expired"] as const).map((f) => {
            const labels: Record<string, string> = { all: "All Status", expiring: "Expiring", expired: "Expired" };
            const counts: Record<string, number> = { all: allItems.length, expiring: expiringCount, expired: expiredCount };
            const active = statusFilter === f;
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? f === "expired"
                      ? "bg-red-600 text-white"
                      : f === "expiring"
                      ? "bg-amber-600 text-white"
                      : isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {labels[f]} <span className={`ml-1 ${active ? "opacity-80" : "opacity-60"}`}>{counts[f]}</span>
              </button>
            );
          })}
        </div>
        <div className={`h-5 w-px ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`} />
        <div className="flex items-center gap-1">
          {(["all", "critical", "soon", "upcoming"] as const).map((f) => {
            const labels: Record<string, string> = { all: "All", critical: "Critical", soon: "Soon", upcoming: "Upcoming" };
            const counts: Record<string, number> = { all: allItems.length, critical: criticalCount, soon: soonCount, upcoming: upcomingCount };
            const active = urgencyFilter === f;
            return (
              <button
                key={f}
                onClick={() => setUrgencyFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {labels[f]} <span className={`ml-1 ${active ? "opacity-80" : "opacity-60"}`}>{counts[f]}</span>
              </button>
            );
          })}
        </div>
        <div className={`h-5 w-px ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`} />
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className={`text-xs rounded-lg px-2.5 py-1.5 border ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
        >
          <option value="all">All Stages</option>
          {STAGE_VALUES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex-1" />
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search drug, patient, barcode..."
            className={`pl-8 pr-3 py-1.5 text-xs rounded-lg border w-56 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
          />
        </div>
        <button
          onClick={() => exportCsv(filteredItems)}
          disabled={filteredItems.length === 0}
          title="Exports the filtered items with patient initials only, excluding NHS Reimb."
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"}`}
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
        <button
          onClick={() => emailData(filteredItems)}
          disabled={filteredItems.length === 0}
          title="Opens your mail client with the filtered items, patient initials only, excluding NHS Reimb."
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
      </div>

      {/* Selection action bar */}
      {selectedIds.size > 0 && createPortal(
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-2xl backdrop-blur-md transition-all ${isDarkMode ? "bg-gray-900/95 border-gray-700" : "bg-white/95 border-gray-200"}`}>
          <span className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{selectedIds.size} selected</span>
          <div className={`w-px h-5 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`} />
          <button
            onClick={() => exportCsv(selectedItems)}
            title="Exports the selected items with patient initials only, excluding NHS Reimb."
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"}`}
          >
            <Download className="w-3.5 h-3.5" /> Export Selected ({selectedIds.size})
          </button>
          <button
            onClick={() => emailData(selectedItems)}
            title="Opens your mail client with the selected items, patient initials only, excluding NHS Reimb."
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            <Mail className="w-3.5 h-3.5" /> Email Selected ({selectedIds.size})
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className={`p-2 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>,
        document.body
      )}

      {/* Table */}
      <div className={`rounded-xl border overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDarkMode ? "bg-gray-800/60" : "bg-gray-50"}>
                <th className="px-3 py-2.5 text-left w-10">
                  <Checkbox checked={selectedIds.size === filteredItems.length && filteredItems.length > 0} onCheckedChange={toggleAll} />
                </th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Days Left</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Type</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Drug</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Patient</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Stage</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>EPS Barcode</th>
                <th className={`px-3 py-2.5 text-right text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>NHS Reimb.</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Rx Date</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Expiry</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-100"}`}>
              {isLoading ? (
                <TableRowsSkeleton isDarkMode={isDarkMode} columns={10} rows={8} />
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={10} className={`px-3 py-12 text-center text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    No items match the current filters
                  </td>
                </tr>
              ) : (
                [...filteredItems]
                  .sort((a, b) => a.daysLeft - b.daysLeft)
                  .map((item) => {
                    const sc = STAGE_COLORS[item.stage] || STAGE_COLORS.Received;
                    const cc = CATEGORY_COLORS[item.category];
                    const window = dispensingWindowDays(item);
                    return (
                      <tr
                        key={item.id}
                        className={`transition-colors ${
                          selectedIds.has(item.id)
                            ? isDarkMode ? "bg-indigo-500/10" : "bg-indigo-50"
                            : isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <Checkbox checked={selectedIds.has(item.id)} onCheckedChange={() => toggleSelect(item.id)} />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${getDaysLeftColor(item.daysLeft)}`}>
                            {getDaysLabel(item.daysLeft)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDarkMode ? cc.bg + " " + cc.text : cc.bgLight + " " + cc.textLight}`}>
                            {CATEGORY_LABELS[item.category]}
                          </span>
                          {item.issueNumber && item.totalIssues && (
                            <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                              Issue {item.issueNumber}/{item.totalIssues} · {window}d window
                            </p>
                          )}
                          {!item.issueNumber && (
                            <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                              {window}d dispense window
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{item.drug}</p>
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.pack} · Qty: {item.quantity}</p>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{item.patient}</p>
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.prescriber}</p>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? sc.bg + " " + sc.text : sc.bgLight + " " + sc.textLight}`}>
                            {item.stage}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-mono ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{item.epsBarcode}</span>
                            <button
                              onClick={() => copyBarcode(item.id, item.epsBarcode)}
                              title="Copy EPS barcode"
                              aria-label={`Copy EPS barcode ${item.epsBarcode}`}
                              className={`p-1 rounded-md transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-200"}`}
                            >
                              {copiedBarcode === item.id
                                ? <Check className="w-3.5 h-3.5 text-green-500" />
                                : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                          £{item.nhsReimbursement.toFixed(2)}
                        </td>
                        <td className={`px-3 py-2.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {fmtShortDate(item.rxDate)}
                        </td>
                        <td className={`px-3 py-2.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {fmtShortDate(item.expiryDate)}
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer summary */}
      <div className={`flex items-center justify-between text-xs py-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
        <span>Showing {filteredItems.length} of {allItems.length} items ({expiringCount} expiring · {expiredCount} expired)</span>
        <span>Total DT: £{totalDtValue.toFixed(2)} · NHS Reimb: £{totalNhsValue.toFixed(2)} · Cost: £{totalCostValue.toFixed(2)} · Margin: £{totalMargin.toFixed(2)}</span>
      </div>
    </div>
  );
}
